// Packages
import React, {
  useCallback,
  useMemo,
  useState,
  Dispatch,
  SetStateAction,
} from 'react';

// Redux
import { useTcoGenerateProductTotals } from 'hooks';
// Components
import { KiteCard, KiteIcon } from '@kite/react-kite';

import { tcoFieldsIds } from 'utils/defaultsAndConstants/constants';
import { tcoFormattedPrice } from 'utils/defaultsAndConstants/tcoConstants';

import DiscountPrice from '../../../images/discount-price.svg';
import Payment from '../../../images/payment.svg';
import './TCOOverview.scss';
import SpectrumDiyTotal from './SpectrumDiyTotal';
import { ITCOSubmission } from 'types';

export interface ITCOOverviewProps {
  tcoSubmissions: ITCOSubmission[];
}

interface IOverviewTableData {
  name: string;
  spectrumPrice: number | string;
  diyPrice: number | string;
}

const TCOOverview = ({ tcoSubmissions }: ITCOOverviewProps) => {
  const { generateTcoProductTotals } = useTcoGenerateProductTotals();
  const {
    termsFieldId,
    noOfSitesFieldId,
    spectrumPromotionsFieldId,
    miscItHoursCostFieldId,
  } = tcoFieldsIds;

  const term =
    parseFloat(
      tcoSubmissions?.find((s) => s.fieldId === termsFieldId)?.answer!
    ) || 0;

  const noOfSites =
    parseFloat(
      tcoSubmissions?.find((s) => s.fieldId === noOfSitesFieldId)?.answer!
    ) || 0;

  const spectrumPromotions =
    parseFloat(
      tcoSubmissions?.find((s) => s.fieldId === spectrumPromotionsFieldId)
        ?.answer!
    ) || 0;

  const miscItHoursCost =
    parseFloat(
      tcoSubmissions?.find((s) => s.fieldId === miscItHoursCostFieldId)?.answer!
    ) || 0;

  const tcoOutputTotals = generateTcoProductTotals({
    format: false,
  });

  const [isOpenOneTimeCost, setIsOpenOneTimeCost] = useState(false);
  const [isOpenMonthlyCost, setIsOpenMonthlyCost] = useState(false);

  const { oneTimeCostData, monthlyCostData } = useMemo(() => {
    const oneTimeCostData = [
      {
        name: 'Equipment Costs',
        spectrumPrice: tcoOutputTotals.spectrumEquipmentCosts,
        diyPrice: tcoOutputTotals.diyEquipmentCosts,
      },
      {
        name: 'Cost of Capital',
        spectrumPrice: tcoOutputTotals.spectrumCostOfCapital,
        diyPrice: tcoOutputTotals.diyCostOfCapital,
      },
      {
        name: 'Implementation Costs',
        spectrumPrice: tcoOutputTotals.spectrumImplementationCosts,
        diyPrice: tcoOutputTotals.diyImplementationCosts,
      },
      {
        name: 'Training',
        spectrumPrice: tcoOutputTotals.spectrumTraining,
        diyPrice: tcoOutputTotals.diyTraining,
      },
    ];

    const monthlyCostData = [
      {
        name: 'Monthly Device & Management Fee',
        spectrumPrice: tcoOutputTotals.spectrumMonthlyDeviceManagementFee,
        diyPrice: tcoOutputTotals.diyMonthlyDeviceManagementFee,
      },
      {
        name: 'Configuration (Adds, Moves, Changes)',
        spectrumPrice: tcoOutputTotals.spectrumConfigurationCostTotal,
        diyPrice: tcoOutputTotals.diyConfigurationCostTotal,
      },
      {
        name: 'Monitoring',
        spectrumPrice: tcoOutputTotals.spectrumMonitoringCostTotal,
        diyPrice: tcoOutputTotals.diyMonitoringCostTotal,
      },
      {
        name: 'Troubleshooting & Resolution',
        spectrumPrice: tcoOutputTotals.spectrumTroubleshootingCostTotal,
        diyPrice: tcoOutputTotals.diyTroubleshootingCostTotal,
      },
      {
        name: 'License Management',
        spectrumPrice: 0,
        diyPrice: tcoOutputTotals.licenseManagementFee,
      },
      {
        name: 'Misc. IT Costs (Hidden Costs)',
        spectrumPrice: 0,
        diyPrice: miscItHoursCost,
      },
    ];
    return {
      oneTimeCostData,
      monthlyCostData,
    };
  }, [tcoOutputTotals, miscItHoursCost]);

  const {
    spectrumOneTimeCost,
    spectrumOngoingMonthlyCost,
    diyOneTimeCost,
    diyOngoingMonthlyCost,
  } = useMemo(() => {
    const spectrumOneTimeCost = oneTimeCostData.reduce(
      (acc, cost) => acc + cost.spectrumPrice,
      0
    );
    const spectrumOngoingMonthlyCost = monthlyCostData.reduce(
      (acc, cost) => acc + cost.spectrumPrice,
      0
    );
    const diyOneTimeCost = oneTimeCostData.reduce(
      (acc, cost) => acc + cost.diyPrice,
      0
    );
    const diyOngoingMonthlyCost = monthlyCostData.reduce(
      (acc, cost) => acc + cost.diyPrice,
      0
    );

    return {
      spectrumOneTimeCost,
      spectrumOngoingMonthlyCost,
      diyOneTimeCost,
      diyOngoingMonthlyCost,
    };
  }, [oneTimeCostData, monthlyCostData]);

  const toggleAccordion =
    (setState: Dispatch<SetStateAction<boolean>>) => () => {
      setState((prev) => !prev);
    };

  // =============================================
  // Helpers (Memo, CB, vars)
  // =============================================

  // =============================================
  // Return
  // =============================================

  const renderTableFooter = useCallback(
    (title: string, isOneTimeCost: boolean) => {
      const spectrumTotal = isOneTimeCost
        ? spectrumOneTimeCost
        : spectrumOngoingMonthlyCost;
      const diyTotal = isOneTimeCost ? diyOneTimeCost : diyOngoingMonthlyCost;

      return (
        <tr>
          <td className="label-col">{title ? `Total ${title}` : ''}</td>
          <td className="spectrum-col">${tcoFormattedPrice(spectrumTotal)}</td>
          <td className="diy-col">${tcoFormattedPrice(diyTotal)}</td>
          <td></td>
        </tr>
      );
    },
    [
      diyOneTimeCost,
      diyOngoingMonthlyCost,
      spectrumOneTimeCost,
      spectrumOngoingMonthlyCost,
    ]
  );

  const renderOverviewTable = useCallback(
    (
      title: string,
      isOpen: boolean,
      toggle: () => void,
      tableData: IOverviewTableData[]
    ) => {
      return (
        <>
          <table className="table">
            <>
              <thead>
                <tr onClick={toggle}>
                  <th className="label-col th-title">{title}</th>
                  <th className="spectrum-col th-header">
                    {isOpen ? '' : 'Spectrum'}
                  </th>
                  <th className="diy-col th-header">{isOpen ? '' : 'DIY'} </th>
                  <th style={{ border: 'none' }}>
                    <button className="arrow-button">
                      <KiteIcon name={isOpen ? 'chevron-up' : 'chevron-down'} />
                    </button>
                  </th>
                </tr>
              </thead>

              {!isOpen && (
                <tbody className="tfooter">
                  {renderTableFooter('', title === 'One-Time Costs')}
                </tbody>
              )}
              {isOpen && (
                <>
                  <tbody>
                    <tr>
                      <td className="label-col"> </td>
                      <td className="spectrum-col th-header">Spectrum</td>
                      <td className="diy-col th-header"> DIY</td>
                      <td></td>
                    </tr>
                    {tableData.map(({ name, spectrumPrice, diyPrice }) => {
                      return (
                        <tr key={`id-${name}`}>
                          <td className="label-col">{name} </td>
                          <td className="spectrum-col">
                            ${tcoFormattedPrice(spectrumPrice)}
                          </td>
                          <td className="diy-col">
                            {' '}
                            ${tcoFormattedPrice(diyPrice)}
                          </td>
                          <td></td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    {renderTableFooter(title, title === 'One-Time Costs')}
                  </tfoot>
                </>
              )}
            </>
          </table>
        </>
      );
    },
    [renderTableFooter]
  );

  return (
    <KiteCard className="overview">
      <div className="overview__header-section">
        <span className="overview__title">Overview</span>
        <span className="overview__sites-label">
          {noOfSites} SITES, {term}-Year Term
        </span>
      </div>

      <div className="overview__table-container">
        {renderOverviewTable(
          'One-Time Costs',
          isOpenOneTimeCost,
          toggleAccordion(setIsOpenOneTimeCost),
          oneTimeCostData
        )}
        {renderOverviewTable(
          'Ongoing Monthly Costs',
          isOpenMonthlyCost,
          toggleAccordion(setIsOpenMonthlyCost),
          monthlyCostData
        )}
      </div>

      <KiteCard className="overview__promotional-discounts">
        <table>
          <tbody>
            <tr>
              <td>
                {' '}
                <img
                  src={Payment}
                  className="icon"
                  alt="payment-icon"
                  width="16"
                  height="16"
                />
                Total Costs
              </td>
              <td>
                $
                {tcoFormattedPrice(
                  spectrumOneTimeCost + spectrumOngoingMonthlyCost * term * 12
                )}
              </td>
              <td>
                $
                {tcoFormattedPrice(
                  diyOneTimeCost + diyOngoingMonthlyCost * term * 12
                )}
              </td>
            </tr>
            <tr>
              <td>
                <img
                  src={DiscountPrice}
                  className="icon"
                  alt="price"
                  width="16"
                  height="16"
                />
                Promotional Discounts
              </td>
              <td>-${tcoFormattedPrice(spectrumPromotions)}</td>
              <td>${tcoFormattedPrice(0)}</td>
            </tr>
          </tbody>
        </table>
      </KiteCard>

      <div className="overview__spectrum-diy-total-container">
        <SpectrumDiyTotal
          title="Spectrum Total"
          primaryCostLabel="Total Cost of Ownership"
          primaryCost={`$${tcoFormattedPrice(
            spectrumOneTimeCost +
              spectrumOngoingMonthlyCost * term * 12 -
              spectrumPromotions
          )}`}
        />
        <SpectrumDiyTotal
          title="DIY total"
          primaryCostLabel="Total Cost of Ownership"
          primaryCost={`$${tcoFormattedPrice(
            diyOneTimeCost + diyOngoingMonthlyCost * term * 12
          )}`}
          isDiy={true}
        />
      </div>
    </KiteCard>
  );
};

export default TCOOverview;
